import { useEffect, useState } from "react";
import ActionButton from "@/components/ActionButton";
import api from "@/lib/api";
import { Plus, Mail, Phone, ExternalLink } from "lucide-react";
import ContactDetailModal from "@/components/ContactDetailModal";

const METIERS = ["bureau_controle", "sps", "graphiste", "enseigniste", "electricien", "plombier", "frigoriste", "menuisier", "metallier", "peintre", "carreleur", "climaticien", "poseur"];

export default function Prestataires() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [show, setShow] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const { data } = await api.get("/prestataires", { params: q ? { q } : {} });
    setList(data);
  };
  useEffect(() => { load(); }, [q]);

  const create = async (e) => {
    e.preventDefault();
    const fd = Object.fromEntries(new FormData(e.target).entries());
    await api.post("/prestataires", fd);
    setShow(false); load();
  };

  return (
    <div className="space-y-6" data-testid="prestataires-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Partenaires opérationnels</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Prestataires</h1>
          <p className="text-sm text-stone-500 mt-1">{list.length} fiche{list.length > 1 ? "s" : ""} · cliquez pour éditer.</p>
        </div>
        <button data-testid="prestataire-new" onClick={() => setShow(true)} className="bg-stone-900 hover:bg-black text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"><Plus size={16}/> Ajouter</button>
      </div>

      <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="prestataire-search" placeholder="Rechercher : nom, métier, mots-clés, notes, ville…" className="max-w-xl w-full border border-stone-200 rounded-lg px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-stone-900"/>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((p) => (
          <div key={p.id} onClick={() => setDetail(p)} className="bg-white border border-stone-200 hover:border-stone-300 rounded-2xl p-5 transition cursor-pointer" data-testid={`prestataire-${p.id}`}>
            <div className="font-display font-semibold text-base">{p.nom}</div>
            {p.societe && p.societe !== p.nom && <div className="text-xs text-stone-500 truncate">{p.societe}</div>}
            <div className="flex flex-wrap gap-1 mt-1.5">
              <span className="pill bg-stone-100 text-stone-700">{p.metier || p.categorie || "—"}</span>
              {(p.types || []).filter((t) => t !== "prestataire").map((t) => (
                <span key={t} className="pill bg-amber-50 text-amber-800 text-[10px] capitalize" data-testid={`pr-${p.id}-also-${t}`}>+ {t}</span>
              ))}
            </div>
            <div className="mt-3 text-sm space-y-1">
              {p.contact && <div className="text-stone-700 truncate">{p.contact}{p.poste && <span className="text-stone-500"> · {p.poste}</span>}</div>}
              {p.email && (
                <a onClick={(e) => e.stopPropagation()} href={`mailto:${p.email}`} data-testid={`pr-mail-${p.id}`} className="inline-flex items-center gap-1 text-xs text-stone-600 hover:text-stone-900 truncate">
                  <Mail size={11}/> {p.email}
                </a>
              )}
              {p.telephone && (
                <a onClick={(e) => e.stopPropagation()} href={`tel:${p.telephone.replace(/\s/g, '')}`} data-testid={`pr-tel-${p.id}`} className="inline-flex items-center gap-1 text-xs text-stone-600 hover:text-stone-900">
                  <Phone size={11}/> {p.telephone}
                </a>
              )}
              {p.site && (
                <a onClick={(e) => e.stopPropagation()} href={p.site} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-900">
                  <ExternalLink size={11}/> {p.site.replace(/^https?:\/\//, "")}
                </a>
              )}
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="col-span-full text-center text-stone-400 py-12">Aucun prestataire.</div>}
      </div>

      {detail && (
        <ContactDetailModal
          item={detail}
          onClose={() => setDetail(null)}
          onSaved={() => load()}
          onDeleted={() => load()}
        />
      )}

      {show && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={create} className="bg-white rounded-2xl p-6 max-w-lg w-full" data-testid="prestataire-form">
            <h3 className="font-display text-2xl font-bold mb-4">Nouveau prestataire</h3>
            <div className="space-y-3 text-sm">
              <input name="nom" required placeholder="Nom contact ou société" className="w-full border border-stone-200 rounded-lg px-3 py-2"/>
              <input name="societe" placeholder="Société (si différente)" className="w-full border border-stone-200 rounded-lg px-3 py-2"/>
              <select name="metier" required className="w-full border border-stone-200 rounded-lg px-3 py-2">
                {METIERS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <input name="categorie" placeholder="Catégorie (ex: Métallerie)" className="w-full border border-stone-200 rounded-lg px-3 py-2"/>
              <div className="grid grid-cols-2 gap-2">
                <input name="contact" placeholder="Contact" className="border border-stone-200 rounded-lg px-3 py-2"/>
                <input name="telephone" placeholder="Téléphone" className="border border-stone-200 rounded-lg px-3 py-2"/>
              </div>
              <input name="email" type="email" placeholder="Email" className="w-full border border-stone-200 rounded-lg px-3 py-2"/>
              <input name="ville" placeholder="Ville" className="w-full border border-stone-200 rounded-lg px-3 py-2"/>
            </div>
            <div className="flex gap-2 mt-5 justify-end">
              <button type="button" onClick={() => setShow(false)} className="px-4 py-2 text-sm border border-stone-200 rounded-lg">Annuler</button>
              <ActionButton type="button" onClick={create} data-testid="prestataire-submit" successLabel="Créé" className="px-4 py-2 text-sm bg-stone-900 text-white rounded-lg font-semibold">Créer</ActionButton>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
